<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-10">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
          class="flex items-center justify-center min-h-full p-4 text-center"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-2xl p-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900"
              >
                {{ title }}
              </DialogTitle>
              <div class="mt-4">
                <div v-if="user" class="mb-4">
                  <p class="text-sm text-gray-600">
                    <strong>User:</strong> {{ user.name }}
                  </p>
                </div>

                <!-- Loading state -->
                <div v-if="loading" class="flex justify-center py-4">
                  <div
                    class="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin"
                  ></div>
                </div>

                <!-- Permissions list -->
                <div
                  v-else-if="allPermissions.length > 0"
                  class="space-y-3 overflow-y-auto max-h-96"
                >
                  <!-- Select All option -->
                  <div
                    class="flex items-center p-3 border rounded-md bg-gray-50"
                  >
                    <input
                      id="select-all-permissions"
                      v-model="selectAll"
                      type="checkbox"
                      class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      @change="toggleSelectAll"
                    />
                    <label
                      for="select-all-permissions"
                      class="flex-1 ml-3 text-sm font-medium text-gray-900"
                    >
                      Select All Permissions
                    </label>
                  </div>

                  <!-- Individual permissions -->
                  <div
                    v-for="permission in allPermissions"
                    :key="permission.id"
                    class="flex items-center"
                  >
                    <input
                      :id="`permission-${permission.id}`"
                      v-model="selectedPermissions"
                      :value="permission.id"
                      type="checkbox"
                      class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      :disabled="isRolePermission(permission.id)"
                    />
                    <label
                      :for="`permission-${permission.id}`"
                      class="flex-1 ml-3 text-sm text-gray-700"
                      :class="{ 'opacity-50': isRolePermission(permission.id) }"
                    >
                      {{ permission.title }}
                      <span
                        v-if="isRolePermission(permission.id)"
                        class="text-xs italic text-gray-500"
                      >
                        (from role)
                      </span>
                    </label>
                  </div>
                </div>

                <div v-else class="py-4 text-sm text-gray-500">
                  No permissions available
                </div>
              </div>

              <div class="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  @click="closeModal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  @click="updatePermissions"
                  :disabled="loading || saving"
                >
                  <span v-if="saving" class="inline-flex items-center">
                    <svg
                      class="w-4 h-4 mr-2 -ml-1 text-white animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </span>
                  <span v-else>Save</span>
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, watch, computed, inject } from "vue";
import { router } from "@inertiajs/vue3";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import Swal from "sweetalert2";
const route = inject('route');

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Manage User Permissions",
  },
  user: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:isOpen", "permissions-updated"]);

const allPermissions = ref([]);
const selectedPermissions = ref([]);
const rolePermissions = ref([]);
const loading = ref(false);
const saving = ref(false);
const selectAll = ref(false);

const isRolePermission = (permissionId) => {
  return rolePermissions.value.includes(permissionId);
};

const toggleSelectAll = () => {
  if (selectAll.value) {
    // Select all permissions that are not from roles
    const availablePermissions = allPermissions.value
      .filter((permission) => !isRolePermission(permission.id))
      .map((permission) => permission.id);
    selectedPermissions.value = [
      ...new Set([...selectedPermissions.value, ...availablePermissions]),
    ];
  } else {
    // Deselect all permissions (keep only role permissions that were already selected)
    selectedPermissions.value = selectedPermissions.value.filter(
      (permissionId) => isRolePermission(permissionId)
    );
  }
};

// Computed property to track if all available permissions are selected
const allAvailableSelected = computed(() => {
  const availablePermissions = allPermissions.value
    .filter((permission) => !isRolePermission(permission.id))
    .map((permission) => permission.id);

  return (
    availablePermissions.length > 0 &&
    availablePermissions.every((permissionId) =>
      selectedPermissions.value.includes(permissionId)
    )
  );
});

// Watch for changes in selected permissions to update selectAll state
watch(
  selectedPermissions,
  () => {
    selectAll.value = allAvailableSelected.value;
  },
  { deep: true }
);

const loadUserPermissions = async () => {
  if (!props.user?.id) return;

  loading.value = true;
  try {
    const response = await fetch(route("users.permissions.get", props.user.id));
    const data = await response.json();

    allPermissions.value = data.all_permissions;
    selectedPermissions.value = [...data.user.direct_permissions];
    rolePermissions.value = [...data.user.role_permissions];
  } catch (error) {
    console.error("Error loading permissions:", error);
    Swal.fire({
      title: "Error!",
      text: "Failed to load user permissions.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    loading.value = false;
  }
};

const updatePermissions = async () => {
  if (!props.user?.id) return;

  saving.value = true;
  try {
    const response = await fetch(
      route("users.permissions.update", props.user.id),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify({
          permissions: selectedPermissions.value,
        }),
      }
    );

    if (response.ok) {
      Swal.fire({
        title: "Success!",
        text: "User permissions updated successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
      emit("permissions-updated"); // Emit event to parent
      closeModal();
    } else {
      throw new Error("Failed to update permissions");
    }
  } catch (error) {
    console.error("Error updating permissions:", error);
    Swal.fire({
      title: "Error!",
      text: "Failed to update user permissions.",
      icon: "error",
      confirmButtonText: "OK",
    });
  } finally {
    saving.value = false;
  }
};

const closeModal = () => {
  emit("update:isOpen", false);
  selectedPermissions.value = [];
  allPermissions.value = [];
  rolePermissions.value = [];
  selectAll.value = false;
};

// Watch for modal open and load permissions
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue && props.user) {
      loadUserPermissions();
    }
  }
);
</script>
